import React, { useContext, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Grid, TextField, MenuItem, Select, FormControl,
  InputLabel, Button, Alert, Chip, Table, TableHead, TableBody,
  TableRow, TableCell, TableContainer,
} from '@mui/material';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { AuthContext } from '../../../context/logeduser';
import {
  useMyForm16List, useRectifications, useCreateRectification,
} from '../../../hooks/useForm16';
import PageHeader from '../PageHeader';
import { SELECTED_MENU_PROPS } from '../../StyledComponents/StyledComponent';

// ---- Visual tokens (shared with the employee Home, Attendance & Payslip dashboards) ----
const CARD_SX = {
  p: { xs: 2, sm: 2.5, md: 3 },
  borderRadius: '22px',
  bgcolor: '#fff',
  border: '1px solid #eef0f4',
  boxShadow: '0 6px 24px rgba(17,24,39,0.05)',
};
const ISSUE_OPTIONS = [
  { value: 'PART_A_TDS', label: 'Part A — TDS mismatch with Form 26AS' },
  { value: 'PAN', label: 'PAN incorrect' },
  { value: 'SALARY', label: 'Salary mismatch' },
  { value: 'DECLARATION', label: 'Declaration / 80C / 80D mismatch' },
  { value: 'OTHER', label: 'Other' },
];

// Soft tinted status chips (matches the rest of the portal).
const STATUS_CHIP = {
  OPEN: { bgcolor: '#fffbeb', color: '#f59e0b' },
  IN_PROGRESS: { bgcolor: '#eef4ff', color: '#2f6bff' },
  RESOLVED: { bgcolor: '#f0fdf4', color: '#16a34a' },
  REJECTED: { bgcolor: '#fef2f2', color: '#ef4444' },
};

export default function RectifyForm() {
  const { authData } = useContext(AuthContext);
  const employeeNo = authData?.employee_no;
  const { data: form16List = [] } = useMyForm16List(employeeNo);
  const myRequestParams = useMemo(() => ({ employee_no: employeeNo }), [employeeNo]);
  const { data: tickets = [], refetch } = useRectifications(myRequestParams);
  const create = useCreateRectification();

  const [form16Id, setForm16Id] = useState('');
  const [issue, setIssue] = useState('PART_A_TDS');
  const [description, setDescription] = useState('');
  const [attachment, setAttachment] = useState(null);

  const submit = () => {
    if (!form16Id || !description) return;
    create.mutate(
      {
        form16: form16Id,
        employee_no_input: employeeNo,
        issue_section: issue,
        description,
        attachment: attachment || undefined,
      },
      {
        onSuccess: () => {
          setDescription('');
          setAttachment(null);
          refetch();
        },
      },
    );
  };

  return (
    <Box sx={{ pb: { xs: 4, md: 2 } }}>
      {/* ---- Header card (shared PageHeader) ---- */}
      <PageHeader
        icon={<BuildCircleIcon />}
        title="Raise a Form 16 Rectification"
        subtitle={(
          <>
            Spotted a mismatch between your Form 16 and Form 26AS / AIS, or an incorrect PAN / salary
            amount? Raise a ticket and HR will review it.
          </>
        )}
      />

      {/* ---- New ticket form ---- */}
      <Paper elevation={0} sx={{ ...CARD_SX, mb: { xs: 2, md: 3 } }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Form 16</InputLabel>
              <Select label="Form 16" value={form16Id} onChange={(e) => setForm16Id(e.target.value)} MenuProps={SELECTED_MENU_PROPS} sx={{ borderRadius: '12px' }}>
                {form16List.length === 0 ? (
                  <MenuItem disabled value="">No Form 16 issued yet</MenuItem>
                ) : (
                  form16List.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      FY {f.fy_label} {f.certificate_no ? `· ${f.certificate_no}` : ''}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Issue Type</InputLabel>
              <Select label="Issue Type" value={issue} onChange={(e) => setIssue(e.target.value)} MenuProps={SELECTED_MENU_PROPS} sx={{ borderRadius: '12px' }}>
                {ISSUE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={4} label="Describe the issue" value={description}
              onChange={(e) => setDescription(e.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" sx={{ display: 'block', mb: 1, color: '#6b7280' }}>Attach proof (optional)</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                component="label"
                size="small"
                variant="outlined"
                startIcon={<AttachFileIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', color: '#6b7280', borderColor: '#e5e7eb' }}
              >
                {attachment ? 'Change file' : 'Choose file'}
                <input type="file" hidden onChange={(e) => setAttachment(e.target.files?.[0] || null)} />
              </Button>
              <Button
                component="label"
                size="small"
                variant="contained"
                disableElevation
                startIcon={<CloudUploadIcon fontSize="small" />}
                sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', background: 'linear-gradient(135deg,#3b82f6,#2f6bff)', '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' } }}
              >
                Upload
                <input type="file" hidden onChange={(e) => setAttachment(e.target.files?.[0] || null)} />
              </Button>
              {attachment && (
                <Typography variant="body2" sx={{ color: '#374151', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 320 }}>
                  {attachment.name}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              disableElevation
              disabled={!form16Id || !description || create.isPending}
              onClick={submit}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: '12px', px: 2.5, background: 'linear-gradient(135deg,#3b82f6,#2f6bff)', '&:hover': { background: 'linear-gradient(135deg,#2f6bff,#2557d6)' } }}
            >
              Submit ticket
            </Button>
            {create.isError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: '14px' }}>
                Failed to create ticket. {create.error?.response?.data?.detail || ''}
              </Alert>
            )}
            {create.isSuccess && <Alert severity="success" sx={{ mt: 2, borderRadius: '14px' }}>Ticket created.</Alert>}
          </Grid>
        </Grid>
      </Paper>

      {/* ---- My tickets ---- */}
      <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 700 }}>My Tickets</Typography>
      <Paper elevation={0} sx={{ ...CARD_SX, p: 0, overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                {['#', 'Created', 'FY', 'Issue', 'Description', 'Status', 'Resolution'].map((h) => (
                  <TableCell key={h} sx={{ fontWeight: 700, color: '#374151', borderColor: '#eef0f4' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.length === 0 ? (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: '#9ca3af', borderColor: '#f1f5f9' }}>No tickets raised yet.</TableCell></TableRow>
              ) : tickets.map((t) => (
                <TableRow key={t.id} hover sx={{ '& td': { borderColor: '#f1f5f9' } }}>
                  <TableCell>{t.id}</TableCell>
                  <TableCell>{t.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>{t.fy_label}</TableCell>
                  <TableCell>{t.issue_section}</TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap>{t.description}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip size="small" label={t.status} sx={{ fontWeight: 700, ...(STATUS_CHIP[t.status] || { bgcolor: '#f3f4f6', color: '#6b7280' }) }} />
                  </TableCell>
                  <TableCell>{t.resolution_notes || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
